import { useEffect, useState } from 'react';
import { Link } from '../router';

type ConsentChoice = 'granted' | 'denied' | null;

declare global {
  interface Window {
    ttuAnalytics?: { choice: () => ConsentChoice; setConsent: (granted: boolean) => void };
  }
}

/** The visitor's analytics choice, kept in sync with public/analytics.js. */
function useAnalyticsConsent() {
  const [choice, setChoice] = useState<ConsentChoice>(() => window.ttuAnalytics?.choice() ?? null);
  useEffect(() => {
    const sync = () => setChoice(window.ttuAnalytics?.choice() ?? null);
    window.addEventListener('ttu-consent-change', sync);
    return () => window.removeEventListener('ttu-consent-change', sync);
  }, []);
  return { choice, available: Boolean(window.ttuAnalytics), setConsent: (granted: boolean) => window.ttuAnalytics?.setConsent(granted) };
}

export function ConsentBanner() {
  const { choice, available, setConsent } = useAnalyticsConsent();
  if (!available || choice !== null) return null;
  return (
    <div role="region" aria-label="Analytics cookies" className="fixed inset-x-3 bottom-3 z-50 rounded-[22px] border border-white/15 bg-night-raised p-5 text-paper shadow-2xl md:left-[108px] md:right-auto md:max-w-md">
      <p className="meta text-muted">Analytics cookies</p>
      <p className="mt-2 text-sm leading-relaxed text-muted">
        Can we use Google Analytics cookies to see how the site is used? Nothing is set unless you accept.{' '}
        <Link href="/cookies" className="font-semibold text-paper underline underline-offset-4">Cookies &amp; storage</Link>
      </p>
      <div className="mt-4 flex gap-2">
        <button type="button" className="button-primary" onClick={() => setConsent(true)}>Accept</button>
        <button type="button" className="button-secondary" onClick={() => setConsent(false)}>Decline</button>
      </div>
    </div>
  );
}

/** Lets a visitor change their analytics choice from the cookies page. */
export function ConsentControls() {
  const { choice, available, setConsent } = useAnalyticsConsent();
  if (!available) return <p>Analytics controls could not load in this browser.</p>;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="meta">Analytics: {choice === 'granted' ? 'accepted' : choice === 'denied' ? 'declined' : 'not chosen'}</span>
      <button type="button" className="button-secondary !border-black/25 !text-ink" disabled={choice === 'granted'} onClick={() => setConsent(true)}>Accept</button>
      <button type="button" className="button-secondary !border-black/25 !text-ink" disabled={choice === 'denied'} onClick={() => setConsent(false)}>Decline</button>
    </div>
  );
}
